// Global app state — the React replacement for Flutter's globals.dart.
// Holds the logged-in user, derived userId + colors, the user's locations and
// the currently-selected location (shared so the persistent header and every
// page agree), and login/logout actions.

import React, {
  createContext, useContext, useMemo, useState, useCallback, useEffect, useRef,
} from 'react';
import {
  loadUserData, saveUserData, clearSession, saveCredentials, loadCredentials,
} from '../utils/storage';
import {
  runSyncIfNeeded, clearCache, isSyncRunning, onSyncDone,
} from '../db/syncService';
import { dbSupported } from '../db/schema';
import { colorsFromUser } from '../theme/buildTheme';
import { disconnectMqtt } from '../mqtt/mqttClient';
import { loadDeviceCatalog } from '../devices/deviceCatalog';
import { setBrowseAll } from './browseAll';
import { detectUiMode } from '../platform';
import { registerPush, resetPushRegistration } from '../push/registerPush';
import { sortByOrder } from '../utils/sortOrder';
import {
  getLocations, getAllLocations, login, getLocationDetail, updateLocationSortOrder,
  getImageById, getFavouriteDevices, getDeviceDetail, setFavourite,
} from '../api/api';
import { withoutHubs } from '../devices/hubDevice';

const AppContext = createContext(null);

// Users.SinkData as the server sends it (number or string). Same test as
// syncService's — a `1` means "your structure changed, pull it again".
const isSinkDirty = (u) => u != null && (u.SinkData === 1 || u.SinkData === '1' || u.SinkData === true);

// A re-login on every foreground return would be pure traffic; once every half
// minute is fast enough for "someone in the house renamed a switch".
const FOREGROUND_REFRESH_MS = 30 * 1000;

// A stored session can re-login (to refresh theme / IsSuperAdmin) only if it
// actually holds usable credentials. Guards against firing the login query with
// undefined / empty / the literal string "undefined" (which produced
// `MobileNumber = undefined and Password = 'undefined'`).
function hasValidCreds(u) {
  const mob = u && u.MobileNumber != null ? String(u.MobileNumber).trim() : '';
  const pass = u && u.Password != null ? String(u.Password).trim() : '';
  const bad = (v) => !v || v.toLowerCase() === 'undefined' || v.toLowerCase() === 'null';
  return /^\d{6,}$/.test(mob) && !bad(pass);
}

export function AppProvider({ children }) {
  const [userData, setUserData] = useState(() => loadUserData());

  // ── Automatic sign-in ─────────────────────────────────────────────────────
  // With saved credentials and no session — a first visit in this browser, a
  // cleared session, an expired one — sign in before showing anything, so the
  // login form never appears to someone who has already signed in here.
  //
  // 'trying' is a THIRD state, distinct from signed-in and signed-out. Without
  // it the router sees "no user" on the first paint and redirects to /login, and
  // a successful sign-in a moment later leaves the user staring at a form they
  // never needed. Guards must wait on this, not just on `user`.
  const [autoLogin, setAutoLogin] = useState(
    () => (!loadUserData() && loadCredentials() ? 'trying' : 'idle'),
  );

  useEffect(() => {
    if (autoLogin !== 'trying') return undefined;
    const creds = loadCredentials();
    if (!creds) { setAutoLogin('idle'); return undefined; }
    let alive = true;
    login(creds.mobileNumber, creds.password)
      .then((res) => {
        const arr = res.data;
        if (!alive) return;
        if (Array.isArray(arr) && arr[0] && arr[0].UserId) {
          // Re-attach the credentials: the server's row is the source of truth
          // for everything else, but its Password field is not guaranteed to
          // come back, and losing it would break the next refresh.
          const merged = [
            { ...arr[0], MobileNumber: creds.mobileNumber, Password: creds.password },
            ...arr.slice(1),
          ];
          saveUserData(merged);
          setUserData(merged);
        }
      })
      // A failure here is not a reason to forget the credentials — the usual
      // cause is no network, and wiping them would mean the one time the user
      // opens the app offline, they are signed out for good.
      .catch(() => {})
      .finally(() => { if (alive) setAutoLogin('done'); });
    return () => { alive = false; };
  }, [autoLogin]);

  const user = userData && userData.length ? userData[0] : null;
  // ── Admin view-as ─────────────────────────────────────────────────────────
  // A Super Admin can look at the app AS another user, for testing. `userId` is
  // what every screen reads to load locations, rooms and devices, so overriding
  // it here is enough — no screen needs to know.
  //
  // ⚠️ THE SIGNED-IN ACCOUNT DOES NOT CHANGE. The session, the password and the
  // Super Admin flag all stay the real user's; only the id things are FETCHED
  // with moves. Otherwise switching to a customer would drop the admin's own
  // rights and strand them there.
  //
  // ⚠️ NOT PERSISTED. It lives for this page load only. An admin who forgets
  // they are impersonating and closes the laptop must not come back tomorrow
  // still looking at someone else's house and thinking it is their own.
  const [viewAsUser, setViewAsUser] = useState(null);
  const [allLocations, setAllLocations] = useState(false);

  const realUserId = user ? Number(user.UserId) : 0;
  // Super Admin (project owner) — gates admin-only features. IsSuperAdmin comes
  // from the Users record on login.
  const isSuperAdmin = String(user?.IsSuperAdmin ?? '') === '1';

  // The Tuya Testing bench is NOT a Super Admin feature — it is a per-user
  // permission, exactly as in Flutter, where the drawer entry was gated on the
  // Users flag and never on IsSuperAdmin. Gating it here on isSuperAdmin alone
  // hid it from every bench operator the backend had already granted it to
  // (e.g. UserId 555: AllowTestingTuya = 1, IsSuperAdmin = 0), and there is no
  // error to see — the tile simply is not drawn.
  // `user` is the SIGNED-IN record, so view-as cannot grant or remove it.
  const canTuyaTest = isSuperAdmin || Number(user?.AllowTestingTuya) === 1;

  // All Locations is an ESTATE-WIDE lens, so a user picked in the dropdown has
  // nothing left to mean: the whole point is to stop scoping by mapping. Asking
  // the server for "user X's rooms in someone else's location" is what returned
  // nothing and printed "No rooms".
  //
  // So the two switches do not stack — All Locations wins, the view-as pick is
  // ignored while it is on (the picker says so), and the reads drop their UserId
  // entirely. See core/state/browseAll.
  const browseAll = isSuperAdmin && allLocations;
  const userId = (!browseAll && viewAsUser) ? Number(viewAsUser.UserId) : realUserId;

  // The gate every room/device read consults. An effect rather than a call in
  // render: it is shared module state, and writing to it while rendering would
  // make the value depend on which component drew first.
  useEffect(() => { setBrowseAll(browseAll); }, [browseAll]);

  // Register this phone for push once we have a signed-in account, and record
  // its FCM token against Users.PushId so the backend can notify it. Keyed on
  // realUserId — the SIGNED-IN account, never a Super Admin's view-as id, since
  // the phone belongs to the real user. Native-only; a no-op on web/desktop.
  // registerPush de-dupes, so re-running on the same id is free.
  useEffect(() => { registerPush(realUserId); }, [realUserId]);
  const colors = useMemo(() => colorsFromUser(user), [user]);
  const companyName = user?.CompanyName || '';

  // Refresh the cached session from the server once on load, so newly-added
  // fields (e.g. IsSuperAdmin) and any changed CompanyInfo/theme propagate
  // without a manual re-login. Uses the stored mobile + password.
  //
  // Best-effort only: skipped silently if the creds aren't usable. The app still
  // runs from the stored session + userId — startup does NOT depend on re-login,
  // and a failure keeps the cached session.
  const refreshSessionRow = useCallback((u) => {
    if (!hasValidCreds(u)) return Promise.resolve(false);
    return login(u.MobileNumber, u.Password)
      .then((res) => {
        const arr = res.data;
        if (!(Array.isArray(arr) && arr[0] && arr[0].UserId)) return false;
        saveUserData(arr);
        setUserData(arr);
        return true;
      })
      .catch(() => false);
  }, []);

  const refreshedRef = useRef(false);
  useEffect(() => {
    if (refreshedRef.current) return;
    const u = userData && userData.length ? userData[0] : null;
    if (!hasValidCreds(u)) return;
    refreshedRef.current = true;
    refreshSessionRow(u);
  }, [userData, refreshSessionRow]);

  // ── Return to foreground: re-read the session row ─────────────────────────
  // A rename or a device change made on ANOTHER phone marks this user
  // `SinkData = 1` on the server (core/db/markSinkData). That flag is only ever
  // read off the session row, so without a re-read here it would be noticed at
  // the next cold launch at the earliest — hours or days later. The sync effect
  // below watches `user` and reacts when the fresh row arrives dirty.
  //
  // Throttled, and only where there is a cache to refresh: the desktop shell
  // reads REST every time (`dbSupported()` is native-only) and has nothing to
  // sync, so a re-login on every tab switch would be pure traffic.
  const lastForegroundRef = useRef(0);
  useEffect(() => {
    if (!dbSupported() || typeof document === 'undefined') return undefined;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastForegroundRef.current < FOREGROUND_REFRESH_MS) return;
      lastForegroundRef.current = now;
      const u = userData && userData.length ? userData[0] : null;
      if (u) refreshSessionRow(u);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [userData, refreshSessionRow]);

  // Profile avatar. Flutter resolves Users.ImageId → ImageUrl once at startup
  // (splashScreen `_backgroundRefresh`) and shows it in every AppBar; the mobile
  // UI does the same. Best-effort — an unresolvable image just leaves it blank.
  const [profileUrl, setProfileUrl] = useState('');
  useEffect(() => {
    const imgId = user && user.ImageId;
    if (imgId == null || imgId === '') { setProfileUrl(''); return undefined; }
    let alive = true;
    getImageById(String(imgId))
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        if (alive && rows[0] && rows[0].ImageUrl) setProfileUrl(rows[0].ImageUrl);
      })
      .catch(() => { /* leave blank */ });
    return () => { alive = false; };
  }, [user]);

  // Favourite devices — the first bottom tab on mobile. Flutter keeps this in a
  // global (FavDeviceList) refreshed after every star toggle; same idea here.
  const [favourites, setFavourites] = useState([]);
  const [favouritesLoading, setFavouritesLoading] = useState(false);
  const [favouritesReady, setFavouritesReady] = useState(false);

  const refreshFavourites = useCallback(async () => {
    if (!userId) { setFavourites([]); return []; }
    setFavouritesLoading(true);
    try {
      const res = await getFavouriteDevices(userId);
      // Sorted HERE rather than in each shell: the desktop favourites page
      // sorted and the mobile favourites tab did not, so the same starred
      // devices came out in two orders. One list, one order.
      // The hub is infrastructure, never a favourite tile (core/devices/hubDevice).
      const list = sortByOrder(withoutHubs(Array.isArray(res.data) ? res.data : []));
      setFavourites(list);
      setFavouritesReady(true);
      return list;
    } catch (e) {
      return [];
    } finally {
      setFavouritesLoading(false);
    }
  }, [userId]);

  useEffect(() => { refreshFavourites(); }, [refreshFavourites]);

  // Star / un-star a device. The mapping row is keyed by DeviceInstalledID, which
  // list endpoints don't always return — resolve it from the device detail when
  // it's missing (Flutter's geInstalledId()).
  const toggleFavourite = useCallback(async (device, isFav) => {
    if (!userId) return false;
    let installedId = device.ID ?? device.DeviceInstalledID;
    if (installedId == null) {
      try {
        const res = await getDeviceDetail(device.UniqueNumber);
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        installedId = row && row.ID;
      } catch (e) { /* fall through */ }
    }
    if (installedId == null) return false;
    // The star changes AT ONCE, on every screen (they all read this list);
    // put back if the server refuses.
    const uno = String(device.UniqueNumber ?? '');
    const before = favourites;
    setFavourites((list) => (isFav
      ? list.filter((d) => String(d.UniqueNumber) !== uno)
      : [...list, device]));
    try {
      await setFavourite(installedId, userId, !isFav);
      await refreshFavourites();
      return true;
    } catch (e) {
      setFavourites(before);
      return false;
    }
  }, [userId, refreshFavourites, favourites]);

  /*
   * ⚠️ IS IT A FAVOURITE? — ONE ANSWER FOR EVERY STAR. Each card used to read
   * `device.favourite` off its own row, and the Favourites page's rows
   * (GetListoffavouriteDevicesbyUserId) carry no such column: every star there
   * was white, and tapping one sent "make favourite" again, so nothing could be
   * removed. The favourites list is the truth; a row's flag is only used until
   * that list has loaded once.
   */
  const favouriteUnos = useMemo(
    () => new Set(favourites.map((d) => String(d.UniqueNumber ?? ''))),
    [favourites],
  );
  const isFavourite = useCallback(
    (device) => (favouritesReady
      ? favouriteUnos.has(String(device?.UniqueNumber ?? device?.uniqueNumber ?? ''))
      : Number(device?.favourite) === 1),
    [favouritesReady, favouriteUnos],
  );

  // ── Local SQLite cache (native only) ──────────────────────────────────────
  // Fill or refresh the cache once per session, BEFORE the screens ask for
  // structure — otherwise the first load races the sync and falls through to
  // REST anyway. runSyncIfNeeded decides whether anything is actually needed
  // (server SinkData flag / empty cache / cache belongs to another user), so on
  // a normal launch this makes no network calls at all.
  //
  // `syncTick` bumps once the sync finishes, so the location load below re-runs
  // and picks up the freshly cached rows.
  //
  // ⚠️ THIS MUST RE-RUN WHEN THE SESSION ROW IS REFRESHED, not only on mount.
  // On mount `user` is the localStorage copy, whose SinkData is whatever it was
  // at LOGIN. The fresh row — from the on-load re-login above, or from a return
  // to the foreground — is what carries a `SinkData = 1` set by another phone
  // (core/db/markSinkData). A once-per-session latch here saw that row arrive
  // and ignored it, so other users' renames were invisible until the next cold
  // launch. The latch is now "ran the mount pass for this user"; a dirty row
  // always gets through. Keyed on the SIGNED-IN user: the cache is theirs,
  // never the view-as user's.
  const [syncTick, setSyncTick] = useState(0);
  // Any full sync — including one a write triggered for this phone (resyncSelf)
  // — replaces the cache under the screens; make them re-read.
  useEffect(() => onSyncDone(() => setSyncTick((n) => n + 1)), []);
  const syncedForRef = useRef(null);
  useEffect(() => {
    if (!realUserId) return;
    const dirty = isSinkDirty(user);
    if (syncedForRef.current === realUserId && !dirty) return;
    if (isSyncRunning()) return;
    syncedForRef.current = realUserId;
    runSyncIfNeeded(user, realUserId)
      .then((ran) => {
        if (!ran) return;
        // (syncTick is bumped by the onSyncDone subscription above.)
        // runSyncIfNeeded cleared the flag on the SERVER; clear the session
        // copy too, or this effect would sync again on every change to `user`.
        if (dirty) {
          setUserData((prev) => {
            if (!prev || !prev.length) return prev;
            const next = [{ ...prev[0], SinkData: '0' }, ...prev.slice(1)];
            saveUserData(next);
            return next;
          });
        }
      })
      .catch(() => { /* cache is optional — never block startup */ });
  }, [user, realUserId]);

  // Locations + the selected location — loaded once per session, shared app-wide
  // so the persistent header's location switcher works on every page.
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  // Distinguishes "load failed" (offer retry) from "account genuinely has none".
  const [locationsError, setLocationsError] = useState(false);
  const autoRetriedRef = useRef(false);

  // ── Common Devices strip visibility ───────────────────────────────────────
  //
  // ONE setting, both shells, persisted — the same rule as "hide offline"
  // (core/hooks/useHideOffline), and for the same reason: it used to be desktop
  // header state that mobile did not read at all, so the phone showed the strip
  // unconditionally while the laptop hid it, from one account, at one moment.
  //
  // ⚠️ THE DEFAULT IS PER-SHELL AND MUST STAY THAT WAY. The two strips are not
  // the same object: desktop's is a full-width row above the rooms sidebar and
  // is genuinely expensive, which is why it was written hidden-by-default "so
  // the room/device area gets the full space"; mobile's is a pinned strip of
  // 88–150px cards that has always been visible and is how common devices are
  // reached at all on a phone — there is no sidebar to reach them from.
  // Collapsing both onto one default would silently hide the strip for every
  // existing phone user, or force a row onto every desktop that had chosen not
  // to have one. So the default is read from the shell and only the STORED
  // value, once the user has actually chosen, applies to both.
  const [showCommon, setShowCommonState] = useState(() => {
    try {
      const saved = localStorage.getItem('showCommon');
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch (e) { /* private mode — fall through to the shell default */ }
    return detectUiMode() === 'mobile';
  });

  // ⚠️ PERSISTED ON CHANGE, NEVER ON MOUNT. Writing this from an effect keyed
  // on the value looks equivalent and is not: the effect also runs on the first
  // render, which stamps whichever shell happened to load FIRST over the
  // per-shell default and turns it into a global one. Open the desktop once and
  // the phone's strip is hidden from then on — the precise regression the
  // split default exists to prevent, arriving a day later and looking like the
  // strip had broken. Only a real toggle writes.
  const setShowCommon = useCallback((next) => {
    setShowCommonState((cur) => {
      const val = typeof next === 'function' ? next(cur) : next;
      try { localStorage.setItem('showCommon', val ? '1' : '0'); } catch (e) { /* ignore */ }
      return val;
    });
  }, []);

  // Reorder mode + who owns the selected location (Super Admin or owner may
  // reorder). Owner = the Location's MasterUserId.
  const [locationOwnerId, setLocationOwnerId] = useState(null);
  const [editOrder, setEditOrder] = useState(false);

  // Load (or reload) the user's locations. Exposed as `retryLocations` so the UI
  // can recover from a failed fetch without a full page reload.
  const loadLocations = useCallback(() => {
    if (!userId) { setLocations([]); setLocationId(null); setLocationsError(false); return; }
    setLocationsLoading(true);
    setLocationsError(false);
    // "All locations" is the admin testing switch: every site on the server,
    // not just the ones mapped to this user.
    //
    // ⚠️ GetAllLocations CARRIES BOTH NAMES, and the bare one is useless here:
    //
    //     LocationName      "Home"                    <- ambiguous across 331 sites
    //     LocationWithName  "Sanjay Ailani-Home"      <- says whose
    //
    // Preferring LocationName (which exists, so it wins any ?? chain) filled the
    // dropdown with a dozen entries called "Home" and "Office" and no way to
    // tell them apart. Whose site it is IS the information when the list is not
    // yours — so the owner-prefixed name is the display name, matching the shape
    // GetLocationsByUserId already returns.
    //
    // The master user's own name comes along on the row (Surname/Name/
    // FatherHusband, MasterUserId), so callers needing it do not have to look
    // it up.
    // ⚠️ `browseAll`, NOT `allLocations` — the flag alone is not a permission.
    // `browseAll` is `isSuperAdmin && allLocations`; the room and device reads
    // have always consulted it (via setBrowseAll -> core/state/browseAll), but
    // THIS read branched on the raw flag, so the only thing standing between a
    // non-admin and `GetAllLocations` — every site on the server, with owner
    // names — was that the desktop header hid the checkbox. Enforcement that
    // lives in a component is enforcement that the next component forgets: the
    // mobile Admin view sheet is a second writer of the same flag, and a third
    // would not have known to check. The two reads now agree, and they agree
    // here rather than at each call site.
    (browseAll
      ? getAllLocations().then((res) => ({
        data: (Array.isArray(res.data) ? res.data : []).map((l) => ({
          ...l,
          LocationName: l.LocationWithName || l.LocationName || `Location ${l.LocationID}`,
          MasterUserName: [l.Surname, l.Name, l.FatherHusband].filter(Boolean).join(' ').trim(),
        })),
      }))
      : getLocations(userId))
      .then((res) => {
        const locs = Array.isArray(res.data) ? res.data : [];
        setLocations(locs);
        // Keep the current pick only if it still exists in the new list —
        // switching user or list otherwise leaves a location selected that the
        // rooms query will return nothing for.
        setLocationId((cur) => (cur != null && locs.some((l) => String(l.LocationID) === String(cur))
          ? cur : (locs.length ? locs[0].LocationID : null)));
      })
      .catch(() => { setLocations([]); setLocationsError(true); })
      .finally(() => setLocationsLoading(false));
  }, [userId, browseAll]);

  // syncTick in the deps: re-read once a sync has populated the cache.
  useEffect(() => { loadLocations(); }, [loadLocations, syncTick]);

  /*
   * The DeviceTypes catalogue — what makes a new switch panel work without an
   * app release. 100 small rows, fetched once and cached in localStorage.
   *
   * ⚠️ `catalogTick` IS WHAT MAKES THE ANSWER APPEAR. `deviceTypeKey` and the
   * channel counts are plain synchronous functions reading a module-level
   * cache, so a catalogue that lands AFTER first paint changes nothing on
   * screen by itself — React has no reason to re-render. Bumping a piece of
   * context state here is that reason, and this provider wraps both shells, so
   * every grid and device screen re-asks. Without it a cold first launch (no
   * seed yet) shows "unsupported" until the user navigates.
   */
  const [catalogTick, setCatalogTick] = useState(0);
  useEffect(() => {
    let alive = true;
    loadDeviceCatalog().then((n) => {
      if (alive && n > 0) setCatalogTick((t) => t + 1);
    });
    return () => { alive = false; };
  }, []);

  // Self-heal a transient load failure: retry once automatically a moment later.
  // Reset the guard whenever a load succeeds so a later failure can retry again.
  useEffect(() => {
    if (locationsError && !autoRetriedRef.current) {
      autoRetriedRef.current = true;
      const t = setTimeout(() => loadLocations(), 2500);
      return () => clearTimeout(t);
    }
    if (!locationsError) autoRetriedRef.current = false;
    return undefined;
  }, [locationsError, loadLocations]);

  // Establish a session. Also records the credentials off the stored row, so
  // every sign-in route — password, OTP, sign-up, device re-binding — enables
  // automatic login without each one having to remember to.
  const setSession = useCallback((arr) => {
    saveUserData(arr);
    const u = Array.isArray(arr) && arr.length ? arr[0] : null;
    if (u && hasValidCreds(u)) saveCredentials(u.MobileNumber, u.Password);
    setUserData(arr);
    setAutoLogin('done');
  }, []);

  // Re-fetch the logged-in user from the server and update the session (applies
  // any changed CompanyInfo/theme). Returns true on success.
  const refreshSession = useCallback(async () => {
    const u = userData && userData.length ? userData[0] : null;
    if (!hasValidCreds(u)) return false;
    try {
      const res = await login(u.MobileNumber, u.Password);
      const arr = res.data;
      if (Array.isArray(arr) && arr[0] && arr[0].UserId) {
        // eslint-disable-next-line no-console
        console.log('[Session] refreshed', { IsSuperAdmin: arr[0].IsSuperAdmin, ButtonColor: arr[0].ButtonColor, BackColor: arr[0].BackColor });
        saveUserData(arr);
        setUserData(arr);
        return true;
      }
    } catch (e) { /* keep current session on failure */ }
    return false;
  }, [userData]);

  const logout = useCallback(() => {
    disconnectMqtt();
    // The next account to sign in on this phone must write the token for itself.
    resetPushRegistration();
    // clearSession() drops the saved sign-in too — otherwise the automatic
    // login would immediately sign the user back in and Logout would appear
    // to do nothing.
    clearSession();
    setAutoLogin('done');
    // Wipe the local cache too. Without this the next person to log in on this
    // phone inherits the previous user's locations, rooms and devices until
    // something forces a re-sync.
    clearCache().catch(() => {});
    syncedForRef.current = null;
    setUserData(null);
    setLocations([]);
    setLocationId(null);
    // Both admin lenses are dropped with the session. They are testing views,
    // not settings, and the next person to sign in on this machine must not
    // inherit a view of somebody else's estate.
    setViewAsUser(null);
    setAllLocations(false);
    setBrowseAll(false);
  }, []);

  // Resolve the selected location's owner (MasterUserId) for the reorder gate.
  // Leaving reorder mode whenever the location changes avoids a stale edit state.
  useEffect(() => {
    setEditOrder(false);
    if (locationId == null) { setLocationOwnerId(null); return undefined; }
    let alive = true;
    getLocationDetail(locationId)
      .then((res) => {
        if (!alive) return;
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        setLocationOwnerId(row ? Number(row.MasterUserId) : null);
      })
      .catch(() => { if (alive) setLocationOwnerId(null); });
    return () => { alive = false; };
  }, [locationId]);

  const isLocationOwner = !!userId && userId === locationOwnerId;
  const canReorder = isSuperAdmin || isLocationOwner;
  // Pushing firmware can brick hardware if the wrong .bin is published, so
  // "Update Code" is not offered to ordinary users with room access — only the
  // project owner and the owner of the location the devices belong to.
  const canUpdateFirmware = isSuperAdmin || isLocationOwner;

  // Reorder the user's locations and persist their per-user SortOrder.
  const reorderLocations = useCallback(async (newList) => {
    setLocations(newList);
    await Promise.all(newList.map((l, i) =>
      updateLocationSortOrder(userId, l.LocationID, i).catch(() => null)));
  }, [userId]);

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.LocationID) === String(locationId)) || null,
    [locations, locationId]
  );

  const value = useMemo(
    () => ({
      userData,
      user,
      userId,
      colors,
      isAuthed: !!user,
      // Admin testing switches — see the view-as note above.
      viewAsUser,
      setViewAsUser,
      realUserId,
      allLocations,
      setAllLocations,
      // True when reads are unscoped. Screens use it to explain WHY they are
      // showing more than the signed-in user owns.
      browseAll,
      // True while a saved sign-in is being used. Guards must treat this as
      // "not yet decided" — NOT as signed-out, or the login form flashes up in
      // front of a user who is about to be signed in.
      signingIn: autoLogin === 'trying',
      // Bumped when the DeviceTypes catalogue lands. Nothing reads its value —
      // being in this object is the point: it re-renders the tree so the
      // synchronous device-type lookups are asked again. See the effect above.
      catalogTick,
      isSuperAdmin,
      canTuyaTest,
      companyName,
      profileUrl,
      favourites,
      favouritesLoading,
      refreshFavourites,
      toggleFavourite,
      isFavourite,
      setSession,
      refreshSession,
      logout,
      locations,
      locationId,
      setLocationId,
      selectedLocation,
      locationsLoading,
      locationsError,
      retryLocations: loadLocations,
      showCommon,
      setShowCommon,
      canReorder,
      canUpdateFirmware,
      locationOwnerId,
      editOrder,
      setEditOrder,
      reorderLocations,
    }),
    [userData, user, userId, colors, isSuperAdmin, canTuyaTest, companyName, profileUrl, favourites, favouritesLoading, refreshFavourites, toggleFavourite, isFavourite, setSession, refreshSession, logout, locations, locationId, selectedLocation, locationsLoading, locationsError, loadLocations, showCommon, setShowCommon, canReorder, canUpdateFirmware, locationOwnerId, editOrder, reorderLocations, autoLogin, viewAsUser, realUserId, allLocations, browseAll, catalogTick]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
